import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { useState } from 'react';
import GeneralDialog from '../../../shared/general-dialog';
import { SharedContactModel } from '../../../../types';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

interface ContactEditAddressDialogProps {
  openAddressDialog: boolean;
  setOpenAddressDialog: (value: boolean) => void;
  clickedIndex: number;
  setValue: UseFormSetValue<Partial<SharedContactModel>>;
  disabled: boolean;
  getValues: UseFormGetValues<Partial<SharedContactModel>>;
}

export default function ContactEditAddressDialog({
  openAddressDialog,
  setOpenAddressDialog,
  clickedIndex,
  setValue,
  disabled,
  getValues,
}: ContactEditAddressDialogProps) {
  const [poBox, setPoBox] = useState(() =>
    getValues(`addresses.${clickedIndex}.addrVal.poBox`)
  );
  const [address, setAddress] = useState(() =>
    getValues(`addresses.${clickedIndex}.addrVal.street`)
  );
  const [extendedAddress, setExtendedAddress] = useState(() =>
    getValues(`addresses.${clickedIndex}.addrVal.neighborhood`)
  );
  const [locality, setLocality] = useState(() =>
    getValues(`addresses.${clickedIndex}.addrVal.city`)
  );
  const [region, setRegion] = useState(() =>
    getValues(`addresses.${clickedIndex}.addrVal.state`)
  );
  const [postalCode, setPostalCode] = useState(() =>
    getValues(`addresses.${clickedIndex}.addrVal.postCode`)
  );
  const [country, setCountry] = useState(() =>
    getValues(`addresses.${clickedIndex}.addrVal.country`)
  );
  const [formatted, setFormatted] = useState(() =>
    getValues(`addresses.${clickedIndex}.addrVal.addressString`)
  );

  const hasPoBox = poBox !== '';
  const hasStreetAddress = address !== '';
  const hasExtendedAddress = extendedAddress !== '';
  const hasLocality = locality !== '';
  const hasRegion = region !== '';
  const hasPostalCode = postalCode !== '';
  const hasCountry = country !== '';

  const formattedValue = `${hasPoBox ? poBox + '\r\n' : ''}${
    hasStreetAddress ? address + '\r\n' : ''
  }${hasExtendedAddress ? extendedAddress + '\r\n' : ''}${
    hasLocality ? locality + ',' : ''
  }${hasRegion ? ` ${region}` : ''}${hasPostalCode ? ` ${postalCode}` : ''}${
    hasCountry ? ` ${country}` : ''
  }`;

  const formValuesHaveChanged =
    poBox !== getValues(`addresses.${clickedIndex}.addrVal.poBox`) ||
    address !== getValues(`addresses.${clickedIndex}.addrVal.street`) ||
    extendedAddress !==
      getValues(`addresses.${clickedIndex}.addrVal.neighborhood`) ||
    locality !== getValues(`addresses.${clickedIndex}.addrVal.city`) ||
    region !== getValues(`addresses.${clickedIndex}.addrVal.state`) ||
    postalCode !== getValues(`addresses.${clickedIndex}.addrVal.postCode`) ||
    country !== getValues(`addresses.${clickedIndex}.addrVal.country`) ||
    formatted !== getValues(`addresses.${clickedIndex}.addrVal.addressString`);

  return (
    <GeneralDialog
      open={openAddressDialog}
      handleClose={() => {
        setOpenAddressDialog(false);
      }}
      title={'Edit Address'}
      dialogContent={
        <Stack sx={{ px: 1 }} gap={4}>
          <Grid container spacing={4}>
            <Grid size={{ xs: 6 }}>
              <TextField
                value={poBox}
                onChange={(e) => {
                  setPoBox(e.target.value);
                }}
                disabled={disabled}
                autoComplete="off"
                label="PO Box"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                value={address}
                onChange={(e) => {
                  setAddress(e.target.value);
                }}
                label="Street Address 1"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                value={extendedAddress}
                onChange={(e) => {
                  setExtendedAddress(e.target.value);
                }}
                label="Street Address 2"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                value={locality}
                onChange={(e) => {
                  setLocality(e.target.value);
                }}
                label="City"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                value={region}
                onChange={(e) => {
                  setRegion(e.target.value);
                }}
                label="State"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                value={postalCode}
                onChange={(e) => {
                  setPostalCode(e.target.value);
                }}
                label="ZIP Code"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                value={country}
                onChange={(e) => {
                  setCountry(e.target.value);
                }}
                label="Country"
                fullWidth
              />
            </Grid>
          </Grid>
          <Stack gap={3}>
            <Tooltip title="Use the fields above to generate formatted address">
              <Button
                onClick={() => {
                  setFormatted(formattedValue);
                }}
                variant="contained"
                sx={{ width: 'fit-content' }}
              >
                Format
              </Button>
            </Tooltip>
            <TextField
              value={formatted}
              onChange={(e) => setFormatted(e.target.value)}
              label="Formatted Address"
              fullWidth
              multiline
              rows={4}
            />
          </Stack>
        </Stack>
      }
      primaryButton={{
        loading: false,
        action: () => {
          if (formValuesHaveChanged) {
            setValue(`addresses.${clickedIndex}.addrVal.poBox`, poBox, {
              shouldDirty: true,
            });
            setValue(`addresses.${clickedIndex}.addrVal.street`, address, {
              shouldDirty: true,
            });
            setValue(
              `addresses.${clickedIndex}.addrVal.neighborhood`,
              extendedAddress,
              { shouldDirty: true }
            );
            setValue(`addresses.${clickedIndex}.addrVal.city`, locality, {
              shouldDirty: true,
            });
            setValue(`addresses.${clickedIndex}.addrVal.state`, region, {
              shouldDirty: true,
            });
            setValue(`addresses.${clickedIndex}.addrVal.postCode`, postalCode, {
              shouldDirty: true,
            });
            setValue(`addresses.${clickedIndex}.addrVal.country`, country, {
              shouldDirty: true,
            });
            setValue(
              `addresses.${clickedIndex}.addrVal.addressString`,
              formatted,
              {
                shouldDirty: true,
              }
            );
          }

          setOpenAddressDialog(false);
        },
        disabled: !formValuesHaveChanged,
        title: 'Update',
      }}
    />
  );
}
